import { ApiClient } from './client';
import type { ApiClientOptions } from './client';
import {
  AuditPage,
  BrandingPublished,
  BrandingRead,
  BrandingRolledBack,
  DeliveryPage,
  DomainChanged,
  DomainList,
  ExecutionDetail,
  ExecutionList,
  FleetPage,
  JobStarted,
  KeyIssued,
  KeyList,
  KeyRevoked,
  ModulesChanged,
  Requeued,
  StatusChanged,
  TenantCreated,
  TenantDetail,
  WebhookList,
  WebhookSet,
} from '../wire';
import type { BrandingDocument, DomainKind, DomainStatus, JobKind, KeyEnv } from '../wire';
import type { TenantStatus } from '@yourstore/domain';

/**
 * The operator surface, as one method per registry row.
 *
 * One per row of `apps/admin-api/src/routes/registry.ts`, in the registry's own
 * order, and **that pairing is the point**. The registry exists so that "what
 * can this service do" is a list somebody can read rather than a grep; a client
 * that assembled URLs at call sites would put half that list back into the
 * views. If a method here has no row there, it is a button that 404s; if a row
 * there has no method here, it is a capability the console cannot reach.
 *
 * There is no method for a client, an appointment or a settlement, because
 * there is no route for one (spec §6.3, "never a data browser"). The console
 * must not even ask — and this file is where "must not ask" is enforceable by
 * reading, since the whole vocabulary of things it can request is here.
 */
export class AdminApi {
  private readonly client: ApiClient;

  constructor(options: ApiClientOptions) {
    this.client = new ApiClient(options);
  }

  /* ---- the fleet and its lifecycle ------------------------------- */

  fleet(options: { limit?: number; offset?: number; status?: string } = {}): Promise<FleetPage> {
    return this.client.request({
      method: 'GET',
      path: '/tenants',
      query: { limit: options.limit, offset: options.offset, status: options.status },
      schema: FleetPage,
    });
  }

  createTenant(body: {
    tenant_id: string;
    name: string;
    timezone: string;
    currency: string;
    status?: string;
    plan?: string;
  }): Promise<TenantCreated> {
    return this.client.request({
      method: 'POST',
      path: '/tenants',
      body,
      schema: TenantCreated,
    });
  }

  tenant(tenantId: string): Promise<TenantDetail> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}`,
      schema: TenantDetail,
    });
  }

  setStatus(tenantId: string, status: TenantStatus): Promise<StatusChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/status`,
      body: { status },
      schema: StatusChanged,
    });
  }

  /**
   * Enable, disable and trial in **one** call.
   *
   * The server validates dependencies against the state the request would leave
   * behind, which is why `clients, services, calendar, partner_api` succeeds as
   * one request and would be refused as four. The console therefore sends what
   * the operator ticked, not one request per tick.
   */
  setModules(
    tenantId: string,
    change: { enable?: string[]; disable?: string[]; trial?: string[] },
  ): Promise<ModulesChanged> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/modules`,
      body: {
        ...(change.enable === undefined || change.enable.length === 0
          ? {}
          : { enable: change.enable }),
        ...(change.disable === undefined || change.disable.length === 0
          ? {}
          : { disable: change.disable }),
        ...(change.trial === undefined || change.trial.length === 0 ? {} : { trial: change.trial }),
      },
      schema: ModulesChanged,
    });
  }

  /* ---- keys ------------------------------------------------------ */

  keys(tenantId: string, includeRevoked = false): Promise<KeyList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys`,
      query: includeRevoked ? { all: '1' } : {},
      schema: KeyList,
    });
  }

  /** The one response that carries a live credential. It is never stored. */
  issueKey(
    tenantId: string,
    body: { integration: string; env: KeyEnv; scopes: string[] },
  ): Promise<KeyIssued> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys`,
      body,
      schema: KeyIssued,
    });
  }

  revokeKey(tenantId: string, keyId: string): Promise<KeyRevoked> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/keys/${encodeURIComponent(keyId)}/revoke`,
      schema: KeyRevoked,
    });
  }

  /* ---- webhooks -------------------------------------------------- */

  webhooks(tenantId: string): Promise<WebhookList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/webhooks`,
      schema: WebhookList,
    });
  }

  /**
   * The destination and the **reference** to its signing secret.
   *
   * `secret_ref` is a Secret Manager resource name. The secret itself is never a
   * request field here for the same reason it is never a column: it would land
   * in a browser's network log, a proxy's access log and the service's request
   * body, three places a shell history is not. The server refuses a value that
   * looks like a secret, and the form says so before the click.
   */
  setWebhook(
    tenantId: string,
    body: {
      integration?: string;
      url: string;
      secret_ref: string;
      signature_header?: string;
      event_types?: string[];
      active?: boolean;
    },
  ): Promise<WebhookSet> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/webhooks`,
      body,
      schema: WebhookSet,
    });
  }

  deliveries(
    tenantId: string,
    options: { limit?: number; offset?: number; status?: string } = {},
  ): Promise<DeliveryPage> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/deliveries`,
      query: { limit: options.limit, offset: options.offset, status: options.status },
      schema: DeliveryPage,
    });
  }

  /** Replay every event since a timestamp (spec §5.6.2). `since` is required. */
  requeue(tenantId: string, since: string): Promise<Requeued> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/deliveries/requeue`,
      body: { since },
      schema: Requeued,
    });
  }

  /* ---- domains (spec §6.4.2) ------------------------------------- */

  domains(tenantId: string): Promise<DomainList> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/domains`,
      schema: DomainList,
    });
  }

  /**
   * Point a hostname at a tenant. Lands at `pending_dns` unless told otherwise.
   *
   * The console cannot move a hostname another tenant holds and cannot remove
   * one: `admin-api` answers `409` for the first and has no route for the
   * second, because both take a working booking site off the air and sign out
   * every guest whose session cookie is scoped to it (spec §6.4.1). Those are
   * `apps/cli`'s, where the hostname is re-typed. That is why there is no
   * `removeDomain` here — the absence is the design, not an omission.
   */
  addDomain(
    tenantId: string,
    body: { hostname: string; kind?: DomainKind; status?: DomainStatus },
  ): Promise<DomainChanged> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/domains`,
      body,
      schema: DomainChanged,
    });
  }

  setDomainStatus(
    tenantId: string,
    hostname: string,
    status: DomainStatus,
  ): Promise<DomainChanged> {
    return this.client.request({
      method: 'PUT',
      path:
        `/tenants/${encodeURIComponent(tenantId)}/domains/` +
        `${encodeURIComponent(hostname)}/status`,
      body: { status },
      schema: DomainChanged,
    });
  }

  /* ---- branding: the theme editor (spec §6.4.3) ------------------- */

  /**
   * The tenant's theme **and the contract vocabulary** the editor renders from.
   *
   * The vocabulary rides the read rather than being a constant in this bundle:
   * one duplicate of the site's token table exists (in `admin-api`) and this
   * console reads it over the wire instead of making a second.
   */
  branding(tenantId: string): Promise<BrandingRead> {
    return this.client.request({
      method: 'GET',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding`,
      schema: BrandingRead,
    });
  }

  /**
   * Publish. The server validates against contract v1, **sanitizes every content
   * zone**, stores only the sanitized bytes and bumps `branding_version`.
   *
   * The response carries the stored document back, and the editor renders *that*
   * rather than what was typed — which is how an operator learns what survives
   * sanitization: by seeing it, on their own words, immediately.
   */
  publishBranding(
    tenantId: string,
    branding: BrandingDocument,
    note?: string,
  ): Promise<BrandingPublished> {
    return this.client.request({
      method: 'PUT',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding`,
      body: { branding, ...(note === undefined || note === '' ? {} : { note }) },
      schema: BrandingPublished,
    });
  }

  /** Restore a kept version's document — as a new, higher version. */
  rollbackBranding(tenantId: string, toVersion?: number): Promise<BrandingRolledBack> {
    return this.client.request({
      method: 'POST',
      path: `/tenants/${encodeURIComponent(tenantId)}/branding/rollback`,
      body: toVersion === undefined ? {} : { to_version: toVersion },
      schema: BrandingRolledBack,
    });
  }

  /* ---- the audit log --------------------------------------------- */

  audit(
    options: {
      tenant_id?: string;
      action?: string;
      actor?: string;
      since?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<AuditPage> {
    return this.client.request({
      method: 'GET',
      path: '/audit',
      query: {
        tenant_id: options.tenant_id,
        action: options.action,
        actor: options.actor,
        since: options.since,
        limit: options.limit,
        offset: options.offset,
      },
      schema: AuditPage,
    });
  }

  /* ---- jobs ------------------------------------------------------ */

  runJob(
    job: JobKind,
    body: { tenant_id: string; execute?: boolean; file?: string; base_url?: string },
  ): Promise<JobStarted> {
    return this.client.request({
      method: 'POST',
      path: `/jobs/${job}/run`,
      body,
      schema: JobStarted,
    });
  }

  executions(job: JobKind, limit = 20): Promise<ExecutionList> {
    return this.client.request({
      method: 'GET',
      path: `/jobs/${job}/executions`,
      query: { limit },
      schema: ExecutionList,
    });
  }

  execution(job: JobKind, executionId: string): Promise<ExecutionDetail> {
    return this.client.request({
      method: 'GET',
      path: `/jobs/${job}/executions/${encodeURIComponent(executionId)}`,
      schema: ExecutionDetail,
    });
  }
}
